import { Dispatch, SetStateAction, useState } from 'react';
import { BannerNotificationListFullResponseDto } from '../types/response';
import { Pagination } from '../../../lib/interfaces/utils';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import Preloader from '../../../ui/components/ui/preloader';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/components/ui/table';
import { PaginationControls } from '../../../ui/components/ui/pagination-controls';
import BannerFormDialog from './banner-form-dialog';
import useApiRequest from '../../../lib/hooks/useRequest';
import ConfirmationDialog from '../../../ui/components/ui/confirmation-dialog';
import { Button } from '../../../ui/components/ui/button';
import { Trash2 } from 'lucide-react';
import { formatDateDetail } from '../../../lib/hooks/utils';

interface DataProps {
  data: BannerNotificationListFullResponseDto[];
  setPagination: Dispatch<SetStateAction<Pagination>>;
  pageCount: number;
  pagination: Pagination;
  isLoading: boolean;
  onRefetch: () => Promise<void>;
}

export const BannerDataTable: React.FC<DataProps> = ({ data, setPagination, pageCount, pagination, isLoading, onRefetch }) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [editingBanner, setEditingBanner] = useState<BannerNotificationListFullResponseDto | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { apiRequest } = useApiRequest();

  const handleEdit = (banner: BannerNotificationListFullResponseDto) => {
    setEditingBanner(banner);
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async (data: { message: string; dateStart: string; dateEnd: string }) => {
    if (!editingBanner) return;
    await apiRequest(`notifications/banner/${editingBanner._id}`, 'PATCH', {
      body: data,
      toast: {
        toastText: 'Banner wurde aktualisiert.'
      }
    });
    await onRefetch();
    setIsEditDialogOpen(false);
    setEditingBanner(null);
  };

  const handleDelete = async (bannerId: string) => {
    await apiRequest(`notifications/banner/${bannerId}`, 'DELETE', {
      toast: {
        toastText: 'Banner wurde gelöscht.'
      }
    });
    await onRefetch();
  };

  const columns: ColumnDef<BannerNotificationListFullResponseDto>[] = [
    {
      id: 'message',
      header: 'Nachricht',
      cell: ({ row }) => (
        <div className="flex items-center min-w-0 w-full">
          <span className="truncate text-sm" title={row.original.message}>
            {row.original.message}
          </span>
        </div>
      )
    },
    {
      id: 'dateStart',
      header: 'Startdatum',
      cell: ({ row }) => (
        <div className="flex items-center whitespace-nowrap">
          <span className="text-sm text-gray-700">{formatDateDetail(row.original.dateStart)}</span>
        </div>
      )
    },
    {
      id: 'dateEnd',
      header: 'Enddatum',
      cell: ({ row }) => (
        <div className="flex items-center whitespace-nowrap">
          <span className="text-sm text-gray-700">{formatDateDetail(row.original.dateEnd)}</span>
        </div>
      )
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div
          className="flex justify-end items-center"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}>
          <ConfirmationDialog
            title="Banner löschen"
            description="Sind Sie sicher, dass Sie dieses Banner unwiderruflich löschen möchten?"
            confirmText="Löschen"
            cancelText="Abbrechen"
            variant="destructive"
            onConfirm={() => handleDelete(row.original._id)}>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-red-50 transition-colors duration-200">
              <Trash2 className="h-4 w-4 text-red-500 transition-colors duration-200" />
            </Button>
          </ConfirmationDialog>
        </div>
      )
    }
  ];

  const table = useReactTable({
    data,
    columns,
    pageCount,
    manualPagination: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    initialState: { pagination: { pageSize: pagination.pageSize } },
    state: {
      pagination,
      sorting,
      rowSelection
    }
  });

  return (
    <div className="w-full">
      <div className="rounded-md overflow-hidden">
        {isLoading ? (
          <Preloader />
        ) : (
          <Table className="border-separate border-spacing-y-4 table-fixed w-full">
            <colgroup>
              <col style={{ width: 'auto' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '80px' }} />
            </colgroup>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => {
                    const isActionsHeader = header.column.id === 'actions';
                    const headerContent = header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext());
                    const isEmpty = !headerContent || (typeof headerContent === 'string' && headerContent.trim() === '') || headerContent === '';

                    return (
                      <TableHead
                        key={header.id}
                        className={`text-[#D0D5DD] h-12 font-semibold ${isActionsHeader ? 'text-right' : ''} ${isEmpty ? 'hidden' : ''}`}>
                        {headerContent}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="bg-white hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                    onClick={() => handleEdit(row.original)}>
                    {row.getVisibleCells().map((cell, j) => {
                      const isActionsCell = cell.column.id === 'actions';
                      return (
                        <TableCell
                          key={cell.id}
                          className={`border-none p-4 align-middle ${j === 0 ? 'rounded-l-[61px]' : ''} ${
                            j === row.getVisibleCells().length - 1 ? 'rounded-r-[61px]' : ''
                          } ${isActionsCell ? 'w-16' : ''}`}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    Keine Banner gefunden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <PaginationControls table={table} />

      <BannerFormDialog
        isOpen={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setEditingBanner(null);
          }
        }}
        onSubmit={handleUpdate}
        banner={editingBanner}
      />
    </div>
  );
};

export default BannerDataTable;
